/**
 * Lesezugriff auf die geöffnete Arbeitsmappe.
 *
 * Wie bei Word und PowerPoint arbeiten alle Prüfungen auf einer Momentaufnahme
 * statt direkt auf Office.js. Excel ist dabei der angenehmste der drei Hosts:
 * Blätter, Bereiche, Tabellen, Diagramme und Namen liegen samt ihrer wichtigsten
 * Eigenschaften bereits bei ExcelApi 1.1.
 *
 * Zwei Excel-Eigenheiten prägen den Aufbau:
 *
 * - `getUsedRange()` wirft auf einem leeren Blatt einen Fehler. Verwendet wird
 *   deshalb durchgehend `getUsedRangeOrNullObject()` mit anschließender Prüfung
 *   auf `isNullObject` – dasselbe Muster gibt es für fast jede Sammlung.
 * - `Range.values` liefert für eine leere Zelle `""`, also dasselbe wie für eine
 *   leere Zeichenkette. Wer Zahl von Text unterscheiden will, braucht
 *   `valueTypes`. Der Kurs dreht sich an mehreren Stellen genau darum, deshalb
 *   führt jede Zelle ihren Werttyp mit.
 *
 * Gelesen wird in Stufen: Was die laufende Excel-Version nicht liefern kann,
 * bleibt `null` und wird in `features` ausdrücklich als nicht gelesen gemeldet.
 */

import { excelApi, supportsExcelApi } from "./capabilities";

/** Obergrenze je Blatt, damit eine große Arbeitsmappe den Abruf nicht sprengt. */
const MAX_ZEILEN = 500;
const MAX_SPALTEN = 60;

/** Werttyp einer Zelle, wie Excel ihn meldet. */
export type Werttyp = "Empty" | "String" | "Integer" | "Double" | "Boolean" | "Error" | "RichValue";

export interface CellInfo {
  /** Adresse ohne Blattnamen, z. B. "B3". */
  address: string;
  /** Zeilennummer wie in Excel angezeigt, also 1-basiert. */
  row: number;
  /** Spaltennummer, 1-basiert: A = 1. */
  column: number;
  /** Rohwert der Zelle. Eine leere Zelle liefert `""`, nicht `null`. */
  value: string | number | boolean;
  /**
   * Formel der Zelle, beginnend mit "=". Enthält die Zelle keine Formel, steht
   * hier der Wert als Text.
   */
  formula: string;
  /** Werttyp – der einzige verlässliche Weg, Zahl von Text zu unterscheiden. */
  valueType: Werttyp;
  /** Zahlenformat als Formatzeichenfolge, z. B. "#.##0,00 €" oder "Standard". */
  numberFormat: string;
  /** Angezeigter Text der Zelle, also das, was tatsächlich zu sehen ist. */
  text: string;
  /**
   * Kategorie des Zahlenformats, wie Excel sie im Dialog anzeigt: "General",
   * "Number", "Currency", "Date", "Percentage", "Text", … `null` vor ExcelApi
   * 1.12. Der Ersatzweg dafür steht in `zahlenformat.ts`.
   */
  numberFormatCategory: string | null;
}

export interface TableColumnInfo {
  name: string;
  /** Nullbasierte Position der Spalte innerhalb der Tabelle. */
  index: number;
}

export interface TableInfo {
  id: string;
  /** Name der Tabelle, über den strukturierte Verweise laufen. */
  name: string;
  /** Name des Blattes, auf dem die Tabelle liegt. */
  sheetName: string;
  /** Adresse des gesamten Tabellenbereichs samt Kopfzeile. */
  address: string | null;
  showHeaders: boolean | null;
  showTotals: boolean | null;
  /** Name der Tabellenformatvorlage, z. B. "TableStyleMedium2". */
  style: string | null;
  /** Filterschaltflächen in der Kopfzeile sichtbar? `null` vor 1.3. */
  showFilterButton: boolean | null;
  /** Verbundene Zeilen (Zebrastreifen)? `null` vor 1.3. */
  showBandedRows: boolean | null;
  columns: TableColumnInfo[];
  /** Anzahl der Datenzeilen ohne Kopf- und Ergebniszeile. */
  rowCount: number;
  /** Ist gerade ein Filter aktiv? `null` vor 1.9. */
  isFiltered: boolean | null;
}

export interface ChartInfo {
  id: string;
  name: string;
  sheetName: string;
  /** "ColumnClustered", "Line", "Pie", … `null` vor 1.7. */
  chartType: string | null;
  /** Text des Diagrammtitels. `null`, wenn kein Titel gesetzt ist. */
  title: string | null;
  /** Ist der Titel eingeblendet? */
  titleVisible: boolean | null;
  /** Ist die Legende eingeblendet? */
  legendVisible: boolean | null;
  /**
   * Anzahl der Datenreihen. Die Zahl der Datenpunkte je Reihe – bei einem
   * Kreisdiagramm also die Zahl der Segmente – bleibt bewusst ungelesen: Sie
   * bräuchte je Diagramm einen eigenen Abruf.
   */
  seriesCount: number | null;
}

export interface NameInfo {
  name: string;
  /** "Workbook" oder "Worksheet". `null` vor 1.4. */
  scope: string | null;
  /** "Range", "String", "Double", … */
  type: string | null;
  /** Worauf der Name verweist, z. B. "=Tabelle1!$B$1". `null` vor 1.7. */
  formula: string | null;
  /** Blatt, auf das der Name begrenzt ist – nur bei blattlokalen Namen. */
  sheetName: string | null;
  /** Adresse des Bereichs, auf den der Name zeigt. */
  rangeAddress: string | null;
}

export interface SheetInfo {
  name: string;
  /** Nullbasierte Position im Register. */
  position: number;
  /** "Visible", "Hidden", "VeryHidden". */
  visibility: string | null;
  /** Registerfarbe als Hexwert. Leer, wenn keine gesetzt ist. `null` vor 1.7. */
  tabColor: string | null;
  /** Gitternetzlinien eingeblendet? `null` vor 1.8. */
  showGridlines: boolean | null;
  /** Ist das Blatt geschützt? `null` vor 1.2. */
  isProtected: boolean | null;
  /** Adresse des fixierten Bereichs, z. B. "A1:D1". `null` = nichts fixiert. */
  frozenRange: string | null;
  /** Adresse des benutzten Bereichs ohne Blattnamen. `null` = Blatt ist leer. */
  usedRangeAddress: string | null;
  /** Alle Zellen des benutzten Bereichs, leere eingeschlossen. */
  cells: CellInfo[];
  tables: TableInfo[];
  charts: ChartInfo[];
}

/** Welche Angaben konnte diese Excel-Version liefern? */
export interface WorkbookFeatures {
  /** Zellinhalte des benutzten Bereichs (ab 1.4). */
  cells: boolean;
  /** Filterschaltflächen und verbundene Zeilen einer Tabelle (ab 1.3). */
  tableDetails: boolean;
  /** Blattschutz (ab 1.2). */
  sheetProtection: boolean;
  /** Fixierte Bereiche und Registerfarbe (ab 1.7). */
  view: boolean;
  /** Gitternetzlinien (ab 1.8). */
  gridlines: boolean;
  /** Diagrammart (ab 1.7). */
  chartType: boolean;
  /** Gültigkeitsbereich und Formel eines Namens (ab 1.4 bzw. 1.7). */
  nameDetails: boolean;
  /** AutoFilter einer Tabelle (ab 1.9). */
  autoFilter: boolean;
  /** Schutz der gesamten Arbeitsmappe (ab 1.7). */
  workbookProtection: boolean;
  /** Kategorie des Zahlenformats je Zelle (ab 1.12). */
  numberFormatCategories: boolean;
}

export interface WorkbookSnapshot {
  sheets: SheetInfo[];
  names: NameInfo[];
  /** Ist die gesamte Arbeitsmappe geschützt? `null` vor 1.7. */
  isProtected: boolean | null;
  features: WorkbookFeatures;
}

/** Fehler, der geworfen wird, wenn das Add-in nicht in Excel läuft. */
export class ExcelUnavailableError extends Error {
  constructor() {
    super(
      "Es ist gerade keine Excel-Arbeitsmappe verbunden. Bitte öffne den Kurs " +
        "über das Add-in-Panel in Excel.",
    );
    this.name = "ExcelUnavailableError";
  }
}

export function isExcelAvailable(): boolean {
  return typeof Excel !== "undefined" && typeof Excel.run === "function";
}

/**
 * Liest eine geladene Eigenschaft ab, ohne die gesamte Prüfung zu sprengen.
 *
 * Office.js wirft beim Zugriff auf eine nicht geladene Eigenschaft einen
 * Fehler. Weil je nach Excel-Version unterschiedlich viel geladen werden
 * konnte, wird jeder Zugriff einzeln abgesichert.
 */
function value<T>(read: () => T): T | null {
  try {
    const result = read();
    return result === undefined ? null : result;
  } catch {
    return null;
  }
}

function text(read: () => unknown): string | null {
  const result = value(read);
  return typeof result === "string" ? result : null;
}

function flag(read: () => unknown): boolean | null {
  const result = value(read);
  return typeof result === "boolean" ? result : null;
}

/** Wandelt eine 1-basierte Spaltennummer in ihren Buchstaben um: 1 → A, 27 → AA. */
export function columnLetter(column: number): string {
  let rest = column;
  let letters = "";
  while (rest > 0) {
    const remainder = (rest - 1) % 26;
    letters = String.fromCharCode(65 + remainder) + letters;
    rest = Math.floor((rest - 1) / 26);
  }
  return letters;
}

/** Zerlegt eine Adresse wie "Tabelle1!B3:D9" in Blattname und Zellbezug. */
export function splitAddress(address: string): { sheet: string | null; range: string } {
  const separator = address.lastIndexOf("!");
  if (separator === -1) return { sheet: null, range: address };
  return {
    sheet: address.slice(0, separator).replace(/^'|'$/g, ""),
    range: address.slice(separator + 1),
  };
}

/** Zeilen- und Spaltennummer der linken oberen Ecke eines Bereichs. */
function rangeOrigin(address: string): { row: number; column: number } {
  const { range } = splitAddress(address);
  const match = /^\$?([A-Z]+)\$?(\d+)/i.exec(range);
  if (!match) return { row: 1, column: 1 };
  const letters = match[1].toUpperCase();
  let column = 0;
  for (const letter of letters) {
    column = column * 26 + (letter.charCodeAt(0) - 64);
  }
  return { row: Number(match[2]), column };
}

/** Liest die gesamte Arbeitsmappe aus und bereitet sie für die Prüfungen auf. */
export async function readWorkbook(): Promise<WorkbookSnapshot> {
  if (!isExcelAvailable()) throw new ExcelUnavailableError();

  const canReadCells = supportsExcelApi(excelApi.benutzterBereich);
  const canReadTableDetails = supportsExcelApi(excelApi.tabellendetails);
  const canReadSheetProtection = supportsExcelApi(excelApi.bereichsdetails);
  const canReadView = supportsExcelApi(excelApi.ansicht);
  const canReadGridlines = supportsExcelApi(excelApi.gitternetz);
  const canReadChartType = supportsExcelApi(excelApi.diagrammart);
  const canReadNameScope = supportsExcelApi(excelApi.namensbereich);
  const canReadNameFormula = supportsExcelApi(excelApi.ansicht);
  const canReadAutoFilter = supportsExcelApi(excelApi.autofilter);
  const canReadWorkbookProtection = supportsExcelApi(excelApi.mappenschutz);
  const canReadFormatCategories = supportsExcelApi(excelApi.formatkategorien);

  const features: WorkbookFeatures = {
    cells: canReadCells,
    tableDetails: canReadTableDetails,
    sheetProtection: canReadSheetProtection,
    view: canReadView,
    gridlines: canReadGridlines,
    chartType: canReadChartType,
    nameDetails: canReadNameScope && canReadNameFormula,
    autoFilter: canReadAutoFilter,
    workbookProtection: canReadWorkbookProtection,
    numberFormatCategories: canReadFormatCategories,
  };

  return Excel.run(async (context) => {
    // Stufe 1: Blätter, Tabellen, Diagramme und Namen – alles ab ExcelApi 1.1.
    const sheetCollection = context.workbook.worksheets;
    const sheetProperties = ["items/name", "items/position", "items/visibility"];
    if (canReadView) sheetProperties.push("items/tabColor");
    if (canReadGridlines) sheetProperties.push("items/showGridlines");
    sheetCollection.load(sheetProperties.join(","));

    const nameCollection = context.workbook.names;
    const nameProperties = ["items/name", "items/type"];
    if (canReadNameScope) nameProperties.push("items/scope");
    if (canReadNameFormula) nameProperties.push("items/formula");
    nameCollection.load(nameProperties.join(","));

    const workbookProtection = context.workbook.protection;
    if (canReadWorkbookProtection) workbookProtection.load("protected");

    await context.sync();

    const sheetItems = sheetCollection.items;

    // Stufe 2: Je Blatt der benutzte Bereich, die Tabellen und die Diagramme.
    const tableProperties = ["items/id", "items/name", "items/showHeaders", "items/showTotals", "items/style"];
    if (canReadTableDetails) {
      tableProperties.push("items/showFilterButton", "items/showBandedRows");
    }

    const chartProperties = ["items/id", "items/name"];
    if (canReadChartType) chartProperties.push("items/chartType");

    const perSheet = sheetItems.map((sheet) => {
      const used = canReadCells ? sheet.getUsedRangeOrNullObject(true) : null;
      if (used) used.load("address,rowCount,columnCount,isNullObject");

      sheet.tables.load(tableProperties.join(","));
      sheet.charts.load(chartProperties.join(","));

      const protection = canReadSheetProtection ? sheet.protection : null;
      if (protection) protection.load("protected");

      const frozen = canReadView ? sheet.freezePanes.getLocationOrNullObject() : null;
      if (frozen) frozen.load("address,isNullObject");

      return { sheet, used, protection, frozen };
    });
    await context.sync();

    // Stufe 3: Zellinhalte, Tabellenspalten, Diagrammtitel und Namensbereiche.
    // Bewusst ein eigener Abruf: Erst jetzt ist bekannt, wie groß der benutzte
    // Bereich je Blatt ist und ob er überhaupt existiert.
    const cellRequests = new Map<number, Excel.Range>();
    for (const [index, entry] of perSheet.entries()) {
      const used = entry.used;
      if (!used || value(() => used.isNullObject) === true) continue;

      const address = text(() => used.address);
      if (!address) continue;

      const rows = Math.min(value(() => used.rowCount) ?? 0, MAX_ZEILEN);
      const columns = Math.min(value(() => used.columnCount) ?? 0, MAX_SPALTEN);
      if (rows <= 0 || columns <= 0) continue;

      const origin = rangeOrigin(address);
      const from = `${columnLetter(origin.column)}${origin.row}`;
      const to = `${columnLetter(origin.column + columns - 1)}${origin.row + rows - 1}`;
      const range = entry.sheet.getRange(`${from}:${to}`);
      const felder = ["address", "values", "formulas", "valueTypes", "numberFormat", "text", "rowCount", "columnCount"];
      if (canReadFormatCategories) felder.push("numberFormatCategories");
      range.load(felder.join(","));
      cellRequests.set(index, range);
    }

    const tableExtras = new Map<string, { columns: Excel.TableColumnCollection; rows: Excel.TableRowCollection; range: Excel.Range; filter: Excel.AutoFilter | null }>();
    for (const entry of perSheet) {
      for (const table of entry.sheet.tables.items) {
        const id = text(() => table.id);
        if (id === null) continue;
        const columns = table.columns;
        columns.load("items/name,items/index");
        const rows = table.rows;
        rows.load("items/index");
        const range = table.getRange();
        range.load("address");
        const filter = canReadAutoFilter ? table.autoFilter : null;
        if (filter) filter.load("enabled,isDataFiltered");
        tableExtras.set(id, { columns, rows, range, filter });
      }
    }

    const chartExtras = new Map<
      string,
      { title: Excel.ChartTitle; legend: Excel.ChartLegend; series: Excel.ChartSeriesCollection }
    >();
    for (const entry of perSheet) {
      for (const chart of entry.sheet.charts.items) {
        const id = text(() => chart.id);
        if (id === null) continue;
        const title = chart.title;
        title.load("text,visible");
        const legend = chart.legend;
        legend.load("visible");
        const series = chart.series;
        series.load("items/name");
        chartExtras.set(id, { title, legend, series });
      }
    }

    const nameRanges = new Map<string, Excel.Range>();
    const nameSheets = new Map<string, Excel.Worksheet>();
    for (const namedItem of nameCollection.items) {
      const name = text(() => namedItem.name);
      if (name === null) continue;
      if (canReadNameScope) {
        const range = namedItem.getRangeOrNullObject();
        range.load("address,isNullObject");
        nameRanges.set(name, range);
        const sheet = namedItem.worksheetOrNullObject;
        sheet.load("name,isNullObject");
        nameSheets.set(name, sheet);
      }
    }

    // Ein einzelner nicht lesbarer Bestandteil darf die übrigen Angaben nicht
    // mitreißen. Scheitert der Abruf, bleiben die betroffenen Felder leer.
    let stufeDreiGelesen = true;
    try {
      await context.sync();
    } catch {
      stufeDreiGelesen = false;
      cellRequests.clear();
      tableExtras.clear();
      chartExtras.clear();
      nameRanges.clear();
      nameSheets.clear();
    }
    if (!stufeDreiGelesen) features.cells = false;

    const sheets: SheetInfo[] = perSheet.map((entry, index) => {
      const sheetName = text(() => entry.sheet.name) ?? `Blatt${index + 1}`;
      const range = cellRequests.get(index);
      // Örtliche Bindung: In den Lesefunktionen unten geht die Einschränkung
      // auf "nicht null" sonst verloren.
      const { protection, frozen, used } = entry;

      return {
        name: sheetName,
        position: value(() => entry.sheet.position) ?? index,
        visibility: text(() => entry.sheet.visibility),
        tabColor: text(() => entry.sheet.tabColor),
        showGridlines: flag(() => entry.sheet.showGridlines),
        isProtected: protection ? flag(() => protection.protected) : null,
        frozenRange:
          frozen && value(() => frozen.isNullObject) !== true
            ? splitAddress(text(() => frozen.address) ?? "").range || null
            : null,
        usedRangeAddress:
          used && value(() => used.isNullObject) !== true
            ? splitAddress(text(() => used.address) ?? "").range || null
            : null,
        cells: range ? readCells(range, canReadFormatCategories) : [],
        tables: entry.sheet.tables.items.map((table) => {
          const id = text(() => table.id) ?? "";
          const extras = tableExtras.get(id);
          return {
            id,
            name: text(() => table.name) ?? "",
            sheetName,
            address: extras ? splitAddress(text(() => extras.range.address) ?? "").range || null : null,
            showHeaders: flag(() => table.showHeaders),
            showTotals: flag(() => table.showTotals),
            style: text(() => table.style),
            showFilterButton: flag(() => table.showFilterButton),
            showBandedRows: flag(() => table.showBandedRows),
            columns: extras
              ? extras.columns.items.map((column, columnIndex) => ({
                  name: text(() => column.name) ?? "",
                  index: value(() => column.index) ?? columnIndex,
                }))
              : [],
            rowCount: extras ? (value(() => extras.rows.items.length) ?? 0) : 0,
            isFiltered: extras?.filter ? flag(() => extras.filter?.isDataFiltered) : null,
          };
        }),
        charts: entry.sheet.charts.items.map((chart) => {
          const id = text(() => chart.id) ?? "";
          const extras = chartExtras.get(id);
          const titleText = extras ? text(() => extras.title.text) : null;
          return {
            id,
            name: text(() => chart.name) ?? "",
            sheetName,
            chartType: text(() => chart.chartType),
            title: titleText !== null && titleText.length > 0 ? titleText : null,
            titleVisible: extras ? flag(() => extras.title.visible) : null,
            legendVisible: extras ? flag(() => extras.legend.visible) : null,
            seriesCount: extras ? (value(() => extras.series.items.length) ?? null) : null,
          };
        }),
      };
    });

    const names: NameInfo[] = nameCollection.items.map((namedItem) => {
      const name = text(() => namedItem.name) ?? "";
      const range = nameRanges.get(name);
      const sheet = nameSheets.get(name);
      return {
        name,
        scope: text(() => namedItem.scope),
        type: text(() => namedItem.type),
        formula: typeof value(() => namedItem.formula) === "string" ? String(namedItem.formula) : null,
        sheetName: sheet && value(() => sheet.isNullObject) !== true ? text(() => sheet.name) : null,
        rangeAddress:
          range && value(() => range.isNullObject) !== true
            ? splitAddress(text(() => range.address) ?? "").range || null
            : null,
      };
    });

    return {
      sheets,
      names,
      isProtected: canReadWorkbookProtection ? flag(() => workbookProtection.protected) : null,
      features,
    };
  });
}

/** Baut aus einem geladenen Bereich die einzelnen Zellen der Momentaufnahme. */
function readCells(range: Excel.Range, mitKategorien: boolean): CellInfo[] {
  const address = text(() => range.address);
  if (address === null) return [];
  const origin = rangeOrigin(address);

  const values = (value(() => range.values) ?? []) as Array<Array<string | number | boolean>>;
  const formulas = (value(() => range.formulas) ?? []) as Array<Array<string | number | boolean>>;
  const valueTypes = (value(() => range.valueTypes) ?? []) as string[][];
  const numberFormats = (value(() => range.numberFormat) ?? []) as Array<Array<string | number>>;
  const texts = (value(() => range.text) ?? []) as string[][];
  const kategorien = mitKategorien ? ((value(() => range.numberFormatCategories) ?? []) as string[][]) : [];

  const cells: CellInfo[] = [];
  for (const [rowIndex, row] of values.entries()) {
    for (const [columnIndex, cellValue] of row.entries()) {
      const column = origin.column + columnIndex;
      cells.push({
        address: `${columnLetter(column)}${origin.row + rowIndex}`,
        row: origin.row + rowIndex,
        column,
        value: cellValue,
        formula: String(formulas[rowIndex]?.[columnIndex] ?? ""),
        valueType: (valueTypes[rowIndex]?.[columnIndex] ?? "Empty") as Werttyp,
        numberFormat: String(numberFormats[rowIndex]?.[columnIndex] ?? "General"),
        text: String(texts[rowIndex]?.[columnIndex] ?? ""),
        numberFormatCategory: kategorien[rowIndex]?.[columnIndex] ?? null,
      });
    }
  }
  return cells;
}

/* ---------- Auswertung der Momentaufnahme ---------- */

/** Alle Zellen der Arbeitsmappe, blattübergreifend. */
export function allCells(snapshot: WorkbookSnapshot): CellInfo[] {
  return snapshot.sheets.flatMap((sheet) => sheet.cells);
}

/** Alle Tabellen der Arbeitsmappe, blattübergreifend. */
export function allTables(snapshot: WorkbookSnapshot): TableInfo[] {
  return snapshot.sheets.flatMap((sheet) => sheet.tables);
}

/** Alle Diagramme der Arbeitsmappe, blattübergreifend. */
export function allCharts(snapshot: WorkbookSnapshot): ChartInfo[] {
  return snapshot.sheets.flatMap((sheet) => sheet.charts);
}

/** Ist die Zelle leer? Excel meldet dafür den Werttyp "Empty". */
export function isEmpty(cell: CellInfo): boolean {
  return cell.valueType === "Empty" || (typeof cell.value === "string" && cell.value.trim() === "");
}

/** Zellen mit sichtbarem Inhalt. */
export function filledCells(snapshot: WorkbookSnapshot): CellInfo[] {
  return allCells(snapshot).filter((cell) => !isEmpty(cell));
}

/**
 * Enthält die Zelle eine echte Zahl?
 *
 * Bewusst über `valueType` statt über `typeof value`: Eine als Text formatierte
 * Zahl liefert bei `values` die Zeichenkette "1234" und wäre sonst nicht von
 * einer echten Zahl zu unterscheiden. Genau dieser Unterschied ist einer der
 * roten Fäden des Kurses.
 */
export function isNumber(cell: CellInfo): boolean {
  return cell.valueType === "Double" || cell.valueType === "Integer";
}

/** Enthält die Zelle Text? Eine leere Zelle zählt nicht als Text. */
export function isText(cell: CellInfo): boolean {
  return cell.valueType === "String" && String(cell.value).trim().length > 0;
}

/** Enthält die Zelle eine Formel? */
export function hasFormula(cell: CellInfo): boolean {
  return cell.formula.startsWith("=");
}

/** Alle Zellen mit Formel, blattübergreifend. */
export function formulaCells(snapshot: WorkbookSnapshot): CellInfo[] {
  return allCells(snapshot).filter(hasFormula);
}

/** Zeigt die Zelle einen Fehlerwert wie #DIV/0! oder #NAME? an? */
export function isError(cell: CellInfo): boolean {
  return cell.valueType === "Error";
}

/** Blatt mit diesem Namen, ohne Rücksicht auf Groß- und Kleinschreibung. */
export function sheetByName(snapshot: WorkbookSnapshot, name: string): SheetInfo | null {
  const gesucht = name.trim().toLowerCase();
  return snapshot.sheets.find((sheet) => sheet.name.trim().toLowerCase() === gesucht) ?? null;
}

/** Zelle an einer bestimmten Adresse, z. B. "B3". */
export function cellAt(sheet: SheetInfo, address: string): CellInfo | null {
  const gesucht = address.replace(/\$/g, "").toUpperCase();
  return sheet.cells.find((cell) => cell.address === gesucht) ?? null;
}

/** Alle Zellen einer Zeile mit sichtbarem Inhalt. */
export function rowCells(sheet: SheetInfo, row: number): CellInfo[] {
  return sheet.cells.filter((cell) => cell.row === row);
}

/** Alle Zellen einer Spalte mit sichtbarem Inhalt. */
export function columnCells(sheet: SheetInfo, column: number): CellInfo[] {
  return sheet.cells.filter((cell) => cell.column === column);
}

/** Blätter, die überhaupt Inhalt tragen. */
export function filledSheets(snapshot: WorkbookSnapshot): SheetInfo[] {
  return snapshot.sheets.filter((sheet) => sheet.cells.some((cell) => !isEmpty(cell)));
}

/**
 * Trägt das Blatt noch seinen Standardnamen?
 *
 * Excel benennt neue Blätter je nach Sprachversion "Tabelle1", "Sheet1" oder
 * "Blatt1". Ein Blatt, das noch so heißt, wurde nie bewusst benannt.
 */
export function hasDefaultSheetName(sheet: SheetInfo): boolean {
  return /^(tabelle|sheet|blatt|feuil|hoja|foglio)\s*\d+$/i.test(sheet.name.trim());
}

/**
 * Trägt die Tabelle noch ihren automatisch vergebenen Namen?
 *
 * Excel nennt neue Tabellen "Tabelle1", "Table1" – abhängig von der
 * Sprachversion. Ein sprechender Name ist die Voraussetzung dafür, dass
 * strukturierte Verweise lesbar bleiben.
 */
export function hasDefaultTableName(table: TableInfo): boolean {
  return /^(tabelle|table|tableau|tabla|tabella)\s*\d+$/i.test(table.name.trim());
}
